import type { PlaybookNodeId } from "@repo/app-types/entities/playbook";
import { flattenPlaybookTreeWithDepth } from "@repo/app-types/entities/playbook";
import type { RecordingAssessmentFeedbackStatus } from "@repo/app-types/entities/recordingAssessmentFeedback";
import { type FC, useCallback, useEffect, useMemo } from "react";
import { Navigate, useSearchParams } from "react-router";
import { appPaths } from "../app/appPaths.js";
import { PlaybookEditorLoadingState } from "./playbookEditorLoadingState.js";
import { usePlaybookEditorOutletContext } from "./playbookEditorOutletContext.js";
import {
  buildManualFeedbackReviewSearchParams,
  resolveFeedbackReviewUrlState,
} from "./playbookEditorUrlState.js";
import {
  buildPlaybookOpenFeedbackCounts,
  getInitialFeedbackReviewNodeId,
} from "./playbookFeedbackReview/feedbackReviewTreeHelpers.js";
import { PlaybookFeedbackReviewTab } from "./playbookFeedbackReview/playbookFeedbackReviewTab.js";

export const PlaybookFeedbackPage: FC = () => {
  const { canReviewFeedback, playbookIdParam } =
    usePlaybookEditorOutletContext();

  return canReviewFeedback ? (
    <EnabledPlaybookFeedbackPage />
  ) : (
    <Navigate
      to={appPaths.settingsPlaybookEdit.build({
        playbookId: playbookIdParam,
      })}
      replace
    />
  );
};

const EnabledPlaybookFeedbackPage: FC = () => {
  const context = usePlaybookEditorOutletContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    data,
    liveTree,
    feedback,
    isFeedbackLoading,
    isFeedbackError,
    onStableSelectionChange,
    onReviewStatusChange,
  } = context;

  const reviewTree = liveTree ?? data?.tree;
  const flattenedNodes = useMemo(
    () => (reviewTree ? flattenPlaybookTreeWithDepth(reviewTree) : []),
    [reviewTree],
  );
  const openFeedbackCounts = useMemo(
    () => buildPlaybookOpenFeedbackCounts(feedback),
    [feedback],
  );
  const fallbackNodeId = useMemo(
    () => getInitialFeedbackReviewNodeId(flattenedNodes, openFeedbackCounts),
    [flattenedNodes, openFeedbackCounts],
  );
  const urlState = useMemo(
    () =>
      resolveFeedbackReviewUrlState({
        currentSearchParams: searchParams,
        flattenedNodes,
        fallbackNodeId,
      }),
    [fallbackNodeId, flattenedNodes, searchParams],
  );
  const currentQuery = searchParams.toString();
  const canonicalQuery = urlState.searchParams.toString();

  useEffect(() => {
    if (
      reviewTree &&
      !isFeedbackLoading &&
      !isFeedbackError &&
      canonicalQuery !== currentQuery
    ) {
      setSearchParams(new URLSearchParams(canonicalQuery), { replace: true });
    }
  }, [
    canonicalQuery,
    currentQuery,
    isFeedbackError,
    isFeedbackLoading,
    reviewTree,
    setSearchParams,
  ]);

  useEffect(() => {
    if (isFeedbackLoading || isFeedbackError) {
      return;
    }
    onStableSelectionChange(urlState.selectedNodeId);
    onReviewStatusChange(urlState.status);
  }, [
    isFeedbackError,
    isFeedbackLoading,
    onReviewStatusChange,
    onStableSelectionChange,
    urlState.selectedNodeId,
    urlState.status,
  ]);

  const handlePickNode = useCallback(
    (nodeId: PlaybookNodeId) => {
      const nextSearchParams = buildManualFeedbackReviewSearchParams({
        currentSearchParams: searchParams,
        selectedNodeId: nodeId,
        status: urlState.status,
      });
      onStableSelectionChange(nodeId);
      setSearchParams(nextSearchParams, { replace: true });
    },
    [onStableSelectionChange, searchParams, setSearchParams, urlState.status],
  );

  const handleStatusChange = useCallback(
    (status: RecordingAssessmentFeedbackStatus) => {
      const nextSearchParams = buildManualFeedbackReviewSearchParams({
        currentSearchParams: searchParams,
        selectedNodeId: urlState.selectedNodeId,
        status,
      });
      onReviewStatusChange(status);
      setSearchParams(nextSearchParams, { replace: true });
    },
    [
      onReviewStatusChange,
      searchParams,
      setSearchParams,
      urlState.selectedNodeId,
    ],
  );

  return (
    <div className="min-h-0 flex-1 overflow-hidden">
      {reviewTree ? (
        <PlaybookFeedbackReviewTab
          feedback={feedback}
          isFeedbackLoading={isFeedbackLoading}
          isFeedbackError={isFeedbackError}
          tree={reviewTree}
          pickedNodeId={urlState.selectedNodeId}
          onPickNode={handlePickNode}
          status={urlState.status}
          onStatusChange={handleStatusChange}
        />
      ) : (
        <PlaybookEditorLoadingState />
      )}
    </div>
  );
};
